import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Building2, Mail, FileDown, Truck, Clock, CheckCircle2 } from "lucide-react";
import { STATE_COORDS } from "../lib/network";
import { formatINR } from "../lib/format";

const W = 460, H = 520;
const LNG0 = 68, LNG1 = 98, LAT0 = 6, LAT1 = 38;
const project = ([lng, lat]) => [
  ((lng - LNG0) / (LNG1 - LNG0)) * W,
  ((LAT1 - lat) / (LAT1 - LAT0)) * H,
];

// Classify an order into an active operations bucket (or null if closed).
const regionStatus = (o) => {
  const f = (o.fulfillment_status || "").toLowerCase();
  if (f === "delivered" || f === "cancelled") return null;
  if (f === "dispatched") return "In-Transit";
  return "Pending";
};

const StatusChip = ({ s }) => (
  <span
    className="inline-flex items-center gap-1 font-grotesk text-[9px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full"
    style={s === "In-Transit"
      ? { color: "#B87333", border: "1px solid #B8733344" }
      : { color: "#D8B23E", border: "1px solid #D8B23E44" }}
  >
    {s === "In-Transit" ? <Truck size={11} /> : <Clock size={11} />}{s}
  </span>
);

export function OperationsMap({ api }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null);
  const [updating, setUpdating] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/orders");
      setOrders(r.data || []);
    } catch {
      toast.error("Could not load orders.");
    }
  }, [api]);

  useEffect(() => {
    (async () => { await refresh(); setLoading(false); })();
  }, [refresh]);

  const markStatus = async (id, status) => {
    setUpdating(id);
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(status === "dispatched" ? "Order dispatched — delivery ETA set." : "Order marked delivered.");
      await refresh();
    } catch {
      toast.error("Could not update order status.");
    } finally {
      setUpdating(null);
    }
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? "" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const active = useMemo(
    () => orders
      .map((o) => ({ ...o, _region: regionStatus(o), _state: (o.shipping_state || "").trim() }))
      .filter((o) => o._region && o._state),
    [orders]
  );

  const byState = useMemo(() => {
    const m = new Map();
    for (const o of active) {
      const s = m.get(o._state) || { state: o._state, orders: [], pending: 0, transit: 0 };
      s.orders.push(o);
      if (o._region === "In-Transit") s.transit += 1; else s.pending += 1;
      m.set(o._state, s);
    }
    return m;
  }, [active]);

  const nodes = useMemo(() => {
    const max = Math.max(1, ...Array.from(byState.values()).map((s) => s.orders.length));
    return Array.from(byState.values())
      .filter((s) => STATE_COORDS[s.state])
      .map((s) => {
        const [x, y] = project(STATE_COORDS[s.state]);
        return { ...s, x, y, r: 6 + (s.orders.length / max) * 12 };
      });
  }, [byState]);

  const downloadInvoice = async (id) => {
    try {
      const res = await api.get(`/orders/${id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `vinci-invoice-${id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download invoice."); }
  };

  const totalPending = active.filter((o) => o._region === "Pending").length;
  const totalTransit = active.filter((o) => o._region === "In-Transit").length;
  const selData = selected ? byState.get(selected) : null;
  const activeNode = hover || (selected ? nodes.find((n) => n.state === selected) : null);

  if (loading) return <p className="font-body text-[#8A8A82]" data-testid="ops-loading">Loading operations…</p>;

  return (
    <div data-testid="operations-map">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="overline text-[#B87333]">/ Operations Map</span>
          <h2 className="font-serif text-3xl md:text-4xl mt-3">Live order coverage</h2>
        </div>
        <div className="flex gap-3">
          <div className="border border-white/10 rounded-sm px-5 py-3">
            <div className="font-serif text-2xl text-[#D8B23E]">{totalPending}</div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82] mt-1">Pending</div>
          </div>
          <div className="border border-white/10 rounded-sm px-5 py-3">
            <div className="font-serif text-2xl text-[#B87333]">{totalTransit}</div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82] mt-1">In-Transit</div>
          </div>
        </div>
      </div>

      {active.length === 0 ? (
        <p className="font-body text-[#8A8A82]" data-testid="ops-empty">No active (Pending / In-Transit) orders right now.</p>
      ) : (
        <div className="grid lg:grid-cols-[1fr_1fr] gap-8 items-start">
          {/* Map */}
          <div className="relative border border-white/10 rounded-sm p-4">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto max-h-[520px] overflow-visible">
              <defs>
                <radialGradient id="opsGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#B87333" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#B87333" stopOpacity="0" />
                </radialGradient>
              </defs>
              {[0.25, 0.5, 0.75].map((f) => (
                <g key={f} stroke="#ffffff" strokeOpacity="0.05">
                  <line x1={f * W} y1="0" x2={f * W} y2={H} />
                  <line x1="0" y1={f * H} x2={W} y2={f * H} />
                </g>
              ))}
              {nodes.map((n, i) => {
                const isSel = selected === n.state;
                const isActive = activeNode?.state === n.state;
                return (
                  <g
                    key={n.state}
                    data-testid={`ops-node-${n.state.replace(/\s+/g, "-").toLowerCase()}`}
                    className="cursor-pointer"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => setSelected(isSel ? null : n.state)}
                  >
                    <motion.circle cx={n.x} cy={n.y} r={n.r + 9} fill="url(#opsGlow)"
                      initial={{ opacity: 0 }} animate={{ opacity: isActive || isSel ? 1 : 0.55 }} transition={{ delay: (i % 12) * 0.03 }} />
                    <motion.circle cx={n.x} cy={n.y} r={n.r}
                      fill={isSel ? "#B87333" : "#0e0e0e"} stroke="#B87333" strokeWidth={isActive || isSel ? 2 : 1}
                      initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 16, delay: (i % 12) * 0.03 }}
                      style={{ transformOrigin: `${n.x}px ${n.y}px` }} />
                    <text x={n.x} y={n.y + 3.5} textAnchor="middle" className="font-grotesk" fontSize="10" fill={isSel ? "#000" : "#B87333"} style={{ pointerEvents: "none" }}>{n.orders.length}</text>
                  </g>
                );
              })}
            </svg>
            {activeNode && (
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-2 bg-[#0e0e0e]/95 border border-[#B87333]/40 rounded-sm px-4 py-2 backdrop-blur" data-testid="ops-tooltip">
                <div className="font-serif text-base text-[#F5F5F0]">{activeNode.state}</div>
                <div className="font-body text-xs text-[#8A8A82] mt-0.5">{activeNode.pending} pending · {activeNode.transit} in-transit</div>
              </div>
            )}
            <p className="font-body text-[10px] text-[#6f6f68] mt-2 text-center">Tap a state node to view its active orders</p>
          </div>

          {/* Detail panel */}
          <div data-testid="ops-panel">
            {!selData ? (
              <div className="border border-white/10 rounded-sm p-6">
                <span className="overline text-[#8A8A82]">Active regions</span>
                <div className="mt-4 space-y-2">
                  {Array.from(byState.values()).sort((a, b) => b.orders.length - a.orders.length).map((s) => (
                    <button
                      key={s.state}
                      data-testid={`ops-region-${s.state.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setSelected(s.state)}
                      className="w-full flex items-center justify-between gap-3 rounded-sm border border-white/10 px-4 py-3 hover:border-[#B87333]/60 transition-colors text-left"
                    >
                      <span className="font-body text-sm text-[#F5F5F0]">{s.state}</span>
                      <span className="font-grotesk text-[10px] uppercase tracking-[0.14em] text-[#8A8A82]">{s.pending} pending · {s.transit} transit</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-2xl">{selData.state} <span className="text-[#8A8A82] text-base">· {selData.orders.length} active</span></h3>
                  <button data-testid="ops-clear" onClick={() => setSelected(null)} className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#8A8A82] hover:text-[#B87333] transition-colors">← All regions</button>
                </div>
                <div className="space-y-3">
                  {selData.orders.map((o) => {
                    const brands = Array.from(new Set((o.items || []).map((it) => it.brand).filter(Boolean)));
                    return (
                      <div key={o.id} data-testid={`ops-order-${o.id}`} className="border border-white/10 rounded-sm p-5 hover:border-white/20 transition-colors">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-3">
                            <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#C9C9C2] border border-white/15 rounded-full px-3 py-1">#{o.id.slice(0, 8).toUpperCase()}</span>
                            <StatusChip s={o._region} />
                          </div>
                          <span className="font-serif text-lg">{formatINR(o.total)}</span>
                        </div>
                        <div className="font-body text-sm text-[#8A8A82] mt-3">{(o.items || []).map((it) => `${it.quantity}× ${it.name}`).join(" · ")}</div>
                        {o.project_name && <div className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#B87333] mt-2">Project · {o.project_name}</div>}
                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 pt-3 border-t border-white/10">
                          <div>
                            <span className="overline text-[#8A8A82] block">Buyer</span>
                            <span className="font-body text-sm text-[#F5F5F0]">{o.shipping_name || o.buyer_email}</span>
                            {o.shipping_phone && <span className="font-body text-xs text-[#8A8A82] block">{o.shipping_phone}</span>}
                            <a data-testid={`ops-buyer-mail-${o.id}`} href={`mailto:${o.buyer_email}?subject=Your Vinci order %23${o.id.slice(0, 8).toUpperCase()}`} className="inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#B87333] hover:underline mt-1"><Mail size={11} /> {o.buyer_email}</a>
                          </div>
                          <div>
                            <span className="overline text-[#8A8A82] block">Supplier / brand</span>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {brands.length ? brands.map((b) => (
                                <span key={b} className="inline-flex items-center gap-1 font-grotesk text-[9px] uppercase tracking-[0.14em] text-[#C9C9C2] border border-white/12 rounded-full px-2.5 py-1"><Building2 size={10} /> {b}</span>
                              )) : <span className="font-body text-xs text-[#6f6f68]">Vinci Inc.</span>}
                            </div>
                            {o.tracking_number && <span className="font-body text-xs text-[#8A8A82] block mt-2">LR/AWB: <span className="text-[#B87333]">{o.tracking_courier} · {o.tracking_number}</span></span>}
                          </div>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          <button data-testid={`ops-invoice-${o.id}`} onClick={() => downloadInvoice(o.id)} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] hover:border-[#B87333] transition-colors"><FileDown size={12} /> Invoice</button>
                          {o._region === "Pending" && (
                            <button data-testid={`ops-dispatch-${o.id}`} disabled={updating === o.id} onClick={() => markStatus(o.id, "dispatched")} className="inline-flex items-center gap-1.5 rounded-full bg-[#B87333] text-black px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] hover:opacity-90 transition-opacity disabled:opacity-50"><Truck size={12} /> {updating === o.id ? "Saving…" : "Mark Dispatched"}</button>
                          )}
                          {o._region === "In-Transit" && (
                            <button data-testid={`ops-deliver-${o.id}`} disabled={updating === o.id} onClick={() => markStatus(o.id, "delivered")} className="inline-flex items-center gap-1.5 rounded-full bg-[#2FB999] text-black px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] hover:opacity-90 transition-opacity disabled:opacity-50"><CheckCircle2 size={12} /> {updating === o.id ? "Saving…" : "Mark Delivered"}</button>
                          )}
                          {o._region === "In-Transit" && o.estimated_delivery && (
                            <span className="font-body text-xs text-[#B87333]">Est. {fmtDate(o.estimated_delivery)}</span>
                          )}
                          <span className="font-body text-xs text-[#6f6f68] ml-auto"><MapPin size={11} className="inline" /> {o._state}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
